import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, Form, Spinner, Alert, Table, Row, Col } from 'react-bootstrap';
import { fetchOneProduct, updateProduct, fetchCategories, fetchStores, fetchSizes } from '../http/productAPI';

const emptyVariant = { size: '', color: '', quantity: 1, barcode: '', storeId: '' };

const EditProductModal = ({ show, onHide, productId, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({});
  const [variants, setVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [newVariant, setNewVariant] = useState({ ...emptyVariant });
  const [currentStore, setCurrentStore] = useState('');
  const fileInputRef = useRef();
  const modalRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Initialize form.quantity for toys when product is loaded
  useEffect(() => {
    if (product && product.type === 'toy') {
      setForm(prev => ({
        ...prev,
        quantity: product.store_quantity ?? product.toy?.quantity ?? product.stock ?? 0
      }));
    }
  }, [product]);

  // Load initial data
  useEffect(() => {
    if (show && productId && mounted.current) {
      const loadData = async () => {
        try {
          setLoading(true);
          setError('');
          
          const [prod, cats, sts] = await Promise.all([
            fetchOneProduct(productId),
            fetchCategories(),
            fetchStores()
          ]);

          if (!mounted.current) return;

          setProduct(prod);
          setCategories(cats);
          setStores(sts);

          // Initialize form data
          setForm({
            name: prod.name || '',
            price: prod.price || '',
            description: prod.description || '',
            category_id: prod.category_id || '',
            type: prod.type || '',
            store_id: prod.stores?.[0]?.id || '',
            article: prod.shoe?.article || prod.clothes?.[0]?.article || '',
            gender: prod.shoe?.gender || '',
            material: prod.clothes?.[0]?.material || prod.toy?.material || '',
            age_rating: prod.toy?.age_rating || '',
            barcode: prod.barcode || prod.toy?.barcode || '',
            discount: prod.discount || 0,
            quantity: prod.type === 'toy' ? (
              prod.store_quantity ??
              prod.variants?.[0]?.quantity ??
              prod.toy?.quantity ??
              prod.stock ??
              0
            ) : undefined
          });

          // Set current store for toys
          if (prod.type === 'toy') {
            const storeObj = prod.product_stores?.[0]?.store;
            setCurrentStore(storeObj ? storeObj.name : 'Не указан');
            setForm(prev => ({
              ...prev,
              store_id: storeObj ? storeObj.id : ''
            }));
          }

          // Set variants
          console.log('[EditProductModal] Загруженные варианты:', prod.variants);
          if (prod.variants && Array.isArray(prod.variants) && prod.variants.length > 0) {
            const mappedVariants = prod.variants.map(v => ({
              ...v,
              name: v.name || v.size || '',
              oldStoreId: v.storeId
            }));
            console.log('[EditProductModal] Обработанные варианты:', mappedVariants);
            setVariants(mappedVariants);
          } else {
            console.log('[EditProductModal] Варианты не найдены или пусты, устанавливаем пустой массив');
            setVariants([]);
          }

          // Set images
          if (prod.images) {
            setImages(prod.images.map(img => ({
              name: img,
              url: img,
              isNew: false
            })));
          }

          if (prod.type === 'shoe' || prod.type === 'clothes') {
            const sizes = await fetchSizes(prod.type, prod.category?.name);
            if (mounted.current) {
              setAvailableSizes(sizes);
            }
          }
        } catch (e) {
          if (mounted.current) {
            setError('Ошибка загрузки товара');
          }
        } finally {
          if (mounted.current) {
            setLoading(false);
          }
        }
      };

      loadData();
    }
  }, [show, productId]);

  // Handle modal close
  const handleClose = () => {
    // Cleanup object URLs
    images.forEach(img => {
      if (img.isNew && img.url) {
        URL.revokeObjectURL(img.url);
      }
    });

    // Reset state
    setLoading(false);
    setSaving(false);
    setError('');
    setProduct(null);
    setCategories([]);
    setStores([]);
    setForm({});
    setVariants([]);
    setImages([]);
    setAvailableSizes([]);
    setNewVariant({ ...emptyVariant });
    setCurrentStore('');

    forceModalCleanup();
    // Снимаем фокус с текущего элемента, чтобы не возвращаться на кнопку "Редактировать"
    if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
      try { document.activeElement.blur(); } catch (_) {}
    }
    // На случай, если браузер восстановит фокус асинхронно — повторим после закрытия
    setTimeout(() => {
      if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
        try { document.activeElement.blur(); } catch (_) {}
      }
    }, 0);
    if (onHide) onHide();
  };

  // Drag & Drop обработка
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    addFiles(files);
  };
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    addFiles(files);
  };
  const addFiles = (files) => {
    const newImgs = files.map(file => ({ file, url: URL.createObjectURL(file), isNew: true }));
    setImages(prev => [...prev, ...newImgs]);
  };
  const handleRemoveImage = (idx) => {
    const img = images[idx];
    setImages(prev => prev.filter((_, i) => i !== idx));
    if (img.isNew) {
    }
  };

  // Обработчик изменения нового варианта
  const handleNewVariantChange = (field, value) => {
    setNewVariant(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Добавление нового варианта
  const handleAddVariant = () => {
    if (!form.store_id) {
      setError('Выберите магазин перед добавлением варианта');
      return;
    }

    // Приоритет: выбранный магазин варианта, иначе текущий магазин формы
    const targetStoreId = newVariant.storeId || form.store_id;

    if (form.type === 'toy') {
      // Вариант игрушки: name, quantity, barcode?, article?
      const trimmedName = (newVariant.name || '').trim();
      const quantityValue = Number(newVariant.quantity ?? 1);

      if (!trimmedName || quantityValue <= 0 || Number.isNaN(quantityValue)) {
        setError('Заполните название варианта и количество');
        return;
      }

      setVariants(prev => {
        const isDuplicate = prev.some(v =>
          (v.name || '').trim() === trimmedName &&
          (v.storeId || form.store_id) === targetStoreId
        );
        if (isDuplicate) {
          setError('Такой вариант уже добавлен для этого магазина (название + магазин)');
          return prev;
        }
        const variantToAdd = {
          ...newVariant,
          name: trimmedName,
          quantity: quantityValue,
          storeId: targetStoreId
        };
        return [...prev, variantToAdd];
      });
    } else {
      // Обувь/одежда
      const quantityValue = Number(newVariant.quantity ?? 1);
      if (!newVariant.size || !newVariant.color || quantityValue <= 0 || Number.isNaN(quantityValue)) {
        setError('Заполните размер, цвет и количество варианта');
        return;
      }
      setVariants(prev => {
        const isDuplicate = prev.some(v => 
          v.size === newVariant.size && 
          v.color === newVariant.color && 
          (v.storeId || form.store_id) === targetStoreId
        );
        if (isDuplicate) {
          setError('Такой вариант уже добавлен в этом магазине (размер, цвет и магазин совпадают)');
          return prev;
        }
        const variantToAdd = { ...newVariant, quantity: quantityValue, storeId: targetStoreId };
        return [...prev, variantToAdd];
      });
    }
    setNewVariant({ ...emptyVariant });
    setError('');
  };

  // Варианты для одежды/обуви/игрушек
  const handleVariantChange = (idx, field, value) => {
    setVariants(prev => prev.map((v, i) => {
      if (i === idx) {
        let updatedValue = value;
        if (field === 'quantity') {
          updatedValue = Math.max(0, parseInt(value, 10) || 0);
        }
        // Если меняется магазин, сохраняем старый storeId
        if (field === 'storeId' && v.storeId !== value) {
          return { ...v, oldStoreId: v.oldStoreId || v.storeId, storeId: value };
        }
        return { ...v, [field]: updatedValue };
      }
      return v;
    }));
  };

  const handleRemoveVariant = (idx) => {
    setVariants(prev => prev.filter((_, i) => i !== idx));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Сохранение
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const data = new FormData();
      data.append('name', form.name);
      data.append('price', form.price);
      data.append('description', form.description);
      data.append('category_id', form.category_id);
      data.append('type', form.type);
      data.append('store_id', form.store_id);
      // Добавляем артикул, если это обувь
      if (form.type === 'shoe' && form.article) {
        data.append('article', form.article);
      }
      // Добавляем пол, если это обувь
      if (form.type === 'shoe' && form.gender) {
        data.append('gender', form.gender);
      }
      // Добавляем материал, если это одежда
      if (form.type === 'clothes' && form.material) {
        data.append('material', form.material);
      }
      // Добавляем артикул для одежды при сохранении
      if (form.type === 'clothes' && form.article) {
        data.append('article', form.article);
      }
      // Сохраняем только имена оставшихся старых фото
      const existingImages = images.filter(img => !img.isNew).map(img => img.name);
      existingImages.forEach(img => data.append('existingImages[]', img));
      // Новые фото
      images.forEach(img => {
        if (img.isNew && img.file) data.append('newImages', img.file);
      });
      // Варианты
      if (form.type === 'shoe' || form.type === 'clothes') {
        const variantsToSend = variants.map(variant => ({
          size: variant.size,
          color: variant.color,
          quantity: variant.quantity,
          barcode: variant.barcode || null,
          storeId: variant.storeId || form.store_id,
          oldStoreId: variant.oldStoreId
        }));
        data.append('variants', JSON.stringify(variantsToSend));
      } else if (form.type === 'toy') {
        // Игрушки: отправляем variants (name, quantity, barcode?, article?, storeId)
        const variantsToSend = variants.length > 0 ? variants.map(v => ({
          name: v.name,
          quantity: v.quantity,
          barcode: v.barcode || null,
          article: v.article || null,
          storeId: v.storeId || form.store_id
        })) : [];
        if (variantsToSend.length > 0) {
          data.append('variants', JSON.stringify(variantsToSend));
        } else {
          // Фоллбек: старый формат, если вариантов нет
          const productStoresData = [{ storeId: form.store_id, quantity: form.quantity }];
          console.log('Отправляем product_stores для игрушки (фоллбек):', productStoresData);
          data.append('product_stores', JSON.stringify(productStoresData));
          data.append('quantity', form.quantity ?? 0);
          data.append('barcode', form.barcode || '');
        }
        data.append('material', form.material || '');
        data.append('age_rating', form.age_rating || '');
      } else {
        data.append('store_id', form.store_id);
      }
      data.append('discount', form.discount || 0);
      await updateProduct(productId, data, true);
      if (onSave) onSave();
      handleClose();
    } catch (e) {
      setError(e.response?.data?.message || 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  // Drag & Drop визуализация
  const dragEvents = {
    onDragOver: e => e.preventDefault(),
    onDrop: handleDrop,
    onClick: () => fileInputRef.current && fileInputRef.current.click(),
    style: { border: '2px dashed #aaa', borderRadius: 8, padding: 16, textAlign: 'center', cursor: 'pointer', marginBottom: 16, background: '#fafafa' }
  };

  // Фильтрация категорий по типу продукта
  const filteredCategories = categories.filter(cat => cat.type === form.type);

  // 1. Функция для получения корректного URL фото
  const getImageUrl = (img) => {
    if (!img) return '';
    if (img.url && img.url.startsWith('http')) return img.url;
    if (img.url) return `${window.location.origin}/static/${img.url.replace(/^\\|\//, '')}`;
    if (typeof img === 'string' && img.startsWith('http')) return img;
    if (typeof img === 'string') return `${window.location.origin}/static/${img.replace(/^\\|\//, '')}`;
    return '';
  };

  const placeholder = '/logo192.PNG'; // или путь к вашей заглушке

  function forceModalCleanup() {
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  useEffect(() => {
    return () => {
      forceModalCleanup();
    };
  }, []);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      ref={modalRef}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      dialogClassName="edit-product-modal"
      restoreFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Редактирование товара</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Spinner animation="border" />
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : product ? (
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Название</Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Цена</Form.Label>
              <Form.Control name="price" type="number" value={form.price} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control as="textarea" name="description" value={form.description} onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Категория</Form.Label>
              <Form.Select
                name="category_id"
                value={form.category_id}
                onChange={handleChange}
              >
                <option value="">Выберите категорию</option>
                {filteredCategories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Тип</Form.Label>
              <Form.Control name="type" value={form.type} onChange={handleChange} disabled />
            </Form.Group>
            {stores.length > 0 && form.type !== 'toy' && (
              <Form.Group className="mb-3">
                <Form.Label>Магазин</Form.Label>
                <Form.Select name="store_id" value={form.store_id} onChange={handleChange} required>
                  <option value="">Выберите магазин</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            {/* Drag & Drop для изображений */}
            <Form.Group className="mb-3">
              <Form.Label>Фотографии</Form.Label>
              <div {...dragEvents}>
                <div>Перетащите изображения сюда или кликните для выбора</div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {images.map((img, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={img.isNew ? img.url : getImageUrl(img)}
                        alt="Фото товара"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
                        onError={e => { e.target.onerror = null; e.target.src = placeholder; }}
                      />
                      <Button size="sm" variant="danger" style={{ position: 'absolute', top: 2, right: 2, padding: '0 6px', borderRadius: '50%' }} onClick={e => { e.stopPropagation(); handleRemoveImage(idx); }}>×</Button>
                    </div>
                  ))}
                </div>
              </div>
            </Form.Group>
            {/* Варианты для одежды/обуви */}
            {(form.type === 'shoe' || form.type === 'clothes') && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Варианты</Form.Label>
                  {/* Форма для добавления нового варианта */}
                  <Row className="align-items-end mb-3">
                    <Col md={3}>
                      <Form.Label>Размер</Form.Label>
                      <Form.Control
                        list="available-sizes-edit-list"
                        value={newVariant.size}
                        onChange={e => handleNewVariantChange('size', e.target.value)}
                        placeholder="Введите или выберите размер"
                        autoComplete="off"
                      />
                      <datalist id="available-sizes-edit-list">
                        {availableSizes.map(size => (
                          <option key={size.id} value={size.value} />
                        ))}
                      </datalist>
                    </Col>
                    <Col md={3}>
                      <Form.Label>Цвет</Form.Label>
                      <Form.Control 
                        value={newVariant.color} 
                        onChange={e => handleNewVariantChange('color', e.target.value)} 
                        placeholder="Цвет" 
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Количество</Form.Label>
                      <Form.Control 
                        type="number" 
                        min={1} 
                        value={newVariant.quantity} 
                        onChange={e => handleNewVariantChange('quantity', Math.max(1, Number(e.target.value)))} 
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Штрих-код</Form.Label>
                      <Form.Control 
                        value={newVariant.barcode} 
                        onChange={e => handleNewVariantChange('barcode', e.target.value)} 
                        placeholder="Штрих-код" 
                      />
                    </Col>
                    <Col md={12} className="mt-2">
                      <Button variant="success" onClick={handleAddVariant}>Добавить вариант</Button>
                    </Col>
                  </Row>
                  {/* Таблица существующих вариантов */}
                  <Table bordered size="sm">
                    <thead>
                      <tr>
                        <th>Размер</th>
                        <th>Цвет</th>
                        <th>Кол-во</th>
                        <th>Штрихкод</th>
                        <th>Магазин</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((v, idx) => (
                        <tr key={idx}>
                          <td><Form.Control value={v.size} onChange={e => handleVariantChange(idx, 'size', e.target.value)} /></td>
                          <td><Form.Control value={v.color} onChange={e => handleVariantChange(idx, 'color', e.target.value)} /></td>
                          <td><Form.Control type="text" value={v.quantity} onChange={e => handleVariantChange(idx, 'quantity', e.target.value)} /></td>
                          <td><Form.Control value={v.barcode} onChange={e => handleVariantChange(idx, 'barcode', e.target.value)} /></td>
                          <td>
                            {(form.type === 'shoe' || form.type === 'clothes') ? (
                              <div className="select-scroll-wrap">
                                <Form.Select
                                  value={v.storeId}
                                  onChange={e => handleVariantChange(idx, 'storeId', e.target.value)}
                                  title={stores.find(store => store.id === v.storeId)?.name || ''}
                                >
                                  <option value="">-</option>
                                  {stores.map(store => (
                                    <option key={store.id} value={store.id} title={store.name}>{store.name}</option>
                                  ))}
                                </Form.Select>
                                {v.storeId && (
                                  <div style={{fontSize: '12px', color: '#888', marginTop: '2px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {stores.find(store => store.id === v.storeId)?.name || ''}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span>{stores.find(store => store.id === v.storeId)?.name || '-'}</span>
                            )}
                          </td>
                          <td><Button size="sm" variant="danger" onClick={() => handleRemoveVariant(idx)}>×</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Form.Group>
                {form.type === 'shoe' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Артикул</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.article}
                        onChange={e => setForm({ ...form, article: e.target.value })}
                        placeholder="Введите артикул обуви"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Пол</Form.Label>
                      <div>
                         <Form.Check
                              inline
                              label="Мужской"
                              type="radio"
                              name="genderOptionsEdit"
                              id="gender-male-edit"
                              value="мужской"
                              checked={form.gender === 'мужской'}
                              onChange={e => setForm({ ...form, gender: e.target.value })}
                         />
                         <Form.Check
                              inline
                              label="Женский"
                              type="radio"
                              name="genderOptionsEdit"
                              id="gender-female-edit"
                              value="женский"
                              checked={form.gender === 'женский'}
                              onChange={e => setForm({ ...form, gender: e.target.value })}
                         />
                      </div>
                    </Form.Group>
                  </>
                )}
              </>
            )}
            {/* Варианты для игрушек */}
            {form.type === 'toy' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Варианты игрушки</Form.Label>
                  <Alert variant="info" className="py-2 mb-2">
                    Добавьте варианты (название, количество, штрих‑код, артикул?, магазин). Если не добавить — будет использован старый способ: одно количество и штрих‑код.
                  </Alert>
                  <Row className="align-items-end mb-3">
                    <Col md={3}>
                      <Form.Label>Название варианта</Form.Label>
                      <Form.Control
                        value={newVariant.name || ''}
                        onChange={e => handleNewVariantChange('name', e.target.value)}
                        placeholder="Напр. Цвет/упаковка"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Количество</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={newVariant.quantity || 1}
                        onChange={e => handleNewVariantChange('quantity', Math.max(1, Number(e.target.value)))}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Штрих‑код</Form.Label>
                      <Form.Control
                        value={newVariant.barcode || ''}
                        onChange={e => handleNewVariantChange('barcode', e.target.value)}
                        placeholder="Штрих‑код (необязательно)"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Артикул</Form.Label>
                      <Form.Control
                        value={newVariant.article || ''}
                        onChange={e => handleNewVariantChange('article', e.target.value)}
                        placeholder="Артикул (необязательно)"
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Label>Магазин</Form.Label>
                      <div className="select-scroll-wrap">
                        <Form.Select
                          value={newVariant.storeId || form.store_id}
                          onChange={e => handleNewVariantChange('storeId', e.target.value)}
                        >
                          <option value="">Выберите магазин</option>
                          {stores.map(store => (
                            <option key={store.id} value={store.id} title={store.name}>{store.name}</option>
                          ))}
                        </Form.Select>
                      </div>
                    </Col>
                    <Col md={12} className="mt-2">
                      <Button variant="success" onClick={handleAddVariant}>Добавить вариант</Button>
                    </Col>
                  </Row>
                  {variants.length > 0 && (
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>Название</th>
                          <th>Кол-во</th>
                          <th>Штрих‑код</th>
                          <th>Артикул</th>
                          <th>Магазин</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v, idx) => (
                          <tr key={idx}>
                            <td><Form.Control value={v.name || ''} onChange={e => handleVariantChange(idx, 'name', e.target.value)} /></td>
                            <td><Form.Control type="number" value={v.quantity || 0} onChange={e => handleVariantChange(idx, 'quantity', Math.max(0, Number(e.target.value)))} /></td>
                            <td><Form.Control value={v.barcode || ''} onChange={e => handleVariantChange(idx, 'barcode', e.target.value)} /></td>
                            <td><Form.Control value={v.article || ''} onChange={e => handleVariantChange(idx, 'article', e.target.value)} /></td>
                            <td>
                              <div className="select-scroll-wrap">
                                <Form.Select
                                  value={v.storeId || form.store_id}
                                  onChange={e => handleVariantChange(idx, 'storeId', e.target.value)}
                                  title={stores.find(store => store.id === (v.storeId || form.store_id))?.name || ''}
                                >
                                  <option value="">-</option>
                                  {stores.map(store => (
                                    <option key={store.id} value={store.id} title={store.name}>{store.name}</option>
                                  ))}
                                </Form.Select>
                                {(v.storeId || form.store_id) && (
                                  <div style={{fontSize: '12px', color: '#888', marginTop: '2px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {stores.find(store => store.id === (v.storeId || form.store_id))?.name || ''}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td><Button size="sm" variant="danger" onClick={() => handleRemoveVariant(idx)}>×</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Form.Group>
              </>
            )}
            {form.type === 'clothes' && (
              <>
                <Form.Group className="mb-3">
                    <Form.Label>Материал</Form.Label>
                    <Form.Control
                        type="text"
                        name="material"
                        value={form.material || ''}
                        onChange={handleChange}
                        placeholder="Введите материал одежды (необязательно)"
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Артикул</Form.Label>
                    <Form.Control
                        type="text"
                        name="article"
                        value={form.article || ''}
                        onChange={handleChange}
                        placeholder="Введите артикул одежды (необязательно)"
                    />
                </Form.Group>
              </>
            )}
            {form.type === 'toy' && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Текущий магазин</Form.Label>
                        <Form.Control
                            type="text"
                            value={currentStore}
                            disabled
                            placeholder="Текущий магазин"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Изменить магазин</Form.Label>
                        <Form.Select name="store_id" value={form.store_id} onChange={handleChange} required>
                            <option value="">Выберите новый магазин</option>
                            {stores.map(store => (
                                <option key={store.id} value={store.id}>{store.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Штрих-код</Form.Label>
                        <Form.Control
                            type="text"
                            name="barcode"
                            value={form.barcode || ''}
                            onChange={handleChange}
                            placeholder="Штрих-код игрушки"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Материал</Form.Label>
                        <Form.Control
                            type="text"
                            name="material"
                            value={form.material || ''}
                            onChange={handleChange}
                            placeholder="Введите материал игрушки (необязательно)"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Возрастные ограничения</Form.Label>
                        <Form.Control
                            type="text"
                            name="age_rating"
                            value={form.age_rating || ''}
                            onChange={handleChange}
                            placeholder="Введите возрастные ограничения (необязательно)"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Количество на складе</Form.Label>
                        <Form.Control
                            type="number"
                            name="quantity"
                            min={0}
                            step={1}
                            value={form.quantity ?? 0}
                            onChange={e => setForm({ ...form, quantity: Math.max(0, Number(e.target.value)) })}
                        />
                    </Form.Group>
                </>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Скидка (%)</Form.Label>
              <Form.Control
                name="discount"
                type="number"
                min={0}
                max={100}
                value={form.discount}
                onChange={handleChange}
                placeholder="Введите процент скидки"
              />
            </Form.Group>
            <div className="d-flex gap-2 mt-3">
              <Button variant="secondary" onClick={handleClose} disabled={saving}>Отмена</Button>
              <Button variant="primary" type="submit" disabled={saving}>Сохранить</Button>
            </div>
          </Form>
        ) : (
          <div>Товар не найден</div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EditProductModal; 